import { DataSource } from '@angular/cdk/table'
import { Lasku, LaskutHakuPyynto, LaskutHakuVastaus } from '@lemon/core-firebase/model/lasku'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { BehaviorSubject, EMPTY, Observable, Subject, from, of } from 'rxjs'
import { debounceTime, map, switchMap } from 'rxjs/operators'
import { LemonTranslationService } from '../../service/lemon-translation.service'
import { TimestampService } from '../../service/timestamp-service'
import { LaskuTransformingWrappingDataSource } from '../transforming-lasku.datasource'

export abstract class LaskuHakuDataSource extends DataSource<Lasku> {
  dataSourceTransformed: LaskuTransformingWrappingDataSource

  private _hakupyyntoSubject = new Subject<LaskutHakuPyynto | null>()
  private _lataaSubject = new BehaviorSubject<boolean>(false)
  private _kokonaismaaraSubject = new BehaviorSubject<number>(0)
  private _edellinenHakuJson: string | null = null

  lataaObservable = this._lataaSubject.asObservable()
  kokonaismaaraObservable = this._kokonaismaaraSubject.asObservable()

  private _laskutObservable: Observable<Lasku[]> = this._hakupyyntoSubject.pipe(
    debounceTime(500),
    switchMap(pyynto => {
      if (!pyynto) {
        return of(this._tyhjenna())
      }
      const pyyntoJson = JSON.stringify(pyynto)
      if (pyyntoJson === this._edellinenHakuJson) {
        this._lataaSubject.next(false)
        return EMPTY
      }
      return from(this._suoritaHaku(pyynto)).pipe(map(vastaus => this._kasitteleHakuvastaus(vastaus, pyyntoJson)))
    }),
  )

  constructor(
    currencyService: CurrencyService,
    dateService: DateService,
    laskuSharedService: LaskuSharedService,
    translationService: LemonTranslationService,
    private _timestampService: TimestampService,
  ) {
    super()
    this.dataSourceTransformed = new LaskuTransformingWrappingDataSource(this, currencyService, dateService, laskuSharedService, translationService)
  }

  protected abstract _kutsuHakua(pyynto: LaskutHakuPyynto): Promise<LaskutHakuVastaus>

  hae(pyynto: LaskutHakuPyynto | null) {
    this._hakupyyntoSubject.next(pyynto)
  }

  connect(): Observable<Lasku[]> {
    this._edellinenHakuJson = null
    return this._laskutObservable
  }

  disconnect() {}

  private async _suoritaHaku(pyynto: LaskutHakuPyynto): Promise<LaskutHakuVastaus | null> {
    this._lataaSubject.next(true)
    try {
      return await this._kutsuHakua(pyynto)
    } catch {
      return null
    }
  }

  private _kasitteleHakuvastaus(vastaus: LaskutHakuVastaus | null, pyyntoJson: string): Lasku[] {
    this._lataaSubject.next(false)
    if (!vastaus || vastaus.e) {
      return this._tyhjenna()
    }
    this._edellinenHakuJson = pyyntoJson
    this._kokonaismaaraSubject.next(vastaus.kokonaismaara ?? 0)
    return this._timestampService.fixTimestamps(vastaus.laskut ?? []) as Lasku[]
  }

  private _tyhjenna(): Lasku[] {
    this._edellinenHakuJson = null
    this._kokonaismaaraSubject.next(0)
    return []
  }
}
